import { useState } from "react";
import { useTemplateStore, type ChannelTemplate } from "../store/templateStore";
import { useSessionStore } from "../store/sessionStore";
import { useAuthStore } from "../store/authStore";
import { newSession, type Session } from "../lib/session";

// TemplatesModal — single entry point for both saving the current
// channel as a template AND spinning up a new channel from a saved
// template. Lives next to Templates ▾ button in the toolbar.

interface Props {
  current?: Session;
  onClose: () => void;
}

export function TemplatesModal({ current, onClose }: Props) {
  const { templates, defaultId, saveTemplate, removeTemplate, setDefault } = useTemplateStore();
  const addChannel = useSessionStore((s) => s.addChannel);
  const creatorName = useAuthStore((s) => s.user?.name ?? "anon");
  const [saveName, setSaveName] = useState("");
  const [applyName, setApplyName] = useState("");
  const [applyingId, setApplyingId] = useState<string | null>(null);

  function handleSave() {
    if (!current) return;
    const name = saveName.trim();
    if (!name) return;
    saveTemplate(name, current);
    setSaveName("");
  }

  function handleApply(t: ChannelTemplate) {
    const title = applyName.trim() || t.name;
    const seed = newSession(title, creatorName);
    const fresh: Session = {
      ...seed,
      mode: t.mode,
      document: t.document,
      attached_documents: t.attached_documents,
      brainstorm_mp_enabled: t.brainstorm_mp_enabled,
      tools_enabled: t.tools_enabled,
      provider_preference: t.provider_preference ?? seed.provider_preference,
      mosh_pit_enabled: t.mosh_pit_enabled ?? seed.mosh_pit_enabled,
    };
    addChannel(fresh);
    onClose();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Channel templates</h2>
        <div className="subtitle">
          Reusable channel shapes — mode, whiteboard seed, attached docs, agent toggles.
          Per-browser; doesn't sync.
        </div>

        {current && (
          <div className="field" style={{ marginTop: 16 }}>
            <label>Save current channel as a template</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="template name (e.g. 'RFC brainstorm')"
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              />
              <button className="btn btn-primary" onClick={handleSave} disabled={!saveName.trim()}>
                Save
              </button>
            </div>
            <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 4 }}>
              Captures: mode {current.mode ? `(${current.mode})` : "(chat)"},
              whiteboard {(current.document ?? "").trim() ? "yes" : "no"},
              {current.attached_documents?.length ?? 0} attached doc{(current.attached_documents?.length ?? 0) === 1 ? "" : "s"},
              brainstorm-MP {current.brainstorm_mp_enabled ? "on" : "off"},
              tools {current.tools_enabled ? "on" : "off"}.
              Does NOT capture chat messages, undo history, or sync target.
            </div>
          </div>
        )}

        <div className="field">
          <label style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span>Spin up a new channel from a template</span>
            {templates.length > 0 && (
              <span style={{ fontSize: 10, color: "var(--fg-dimmer)" }}>{templates.length} saved</span>
            )}
          </label>
          {templates.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--fg-dim)", padding: "12px 0", textAlign: "center" }}>
              No templates saved yet.
            </div>
          ) : (
            <>
              <input
                type="text"
                value={applyName}
                onChange={(e) => setApplyName(e.target.value)}
                placeholder="optional channel name (defaults to template name)"
                style={{ marginBottom: 8 }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
                {templates.map((t) => (
                  <div key={t.id} className="tpl-row">
                    <button
                      className="tpl-row-star"
                      onClick={() => setDefault(t.id)}
                      title={defaultId === t.id
                        ? "Default for new channels — click to unset"
                        : "Make this the default template for new channels (+)"}
                      aria-label={defaultId === t.id ? `Unset default template ${t.name}` : `Set ${t.name} as default template`}
                      style={{ color: defaultId === t.id ? "var(--accent)" : "var(--fg-dimmer)" }}
                    >
                      {defaultId === t.id ? "★" : "☆"}
                    </button>
                    <div className="tpl-row-main">
                      <div className="tpl-row-name">{t.name}</div>
                      <div className="tpl-row-summary">
                        {summarize(t)}{defaultId === t.id && " · default"}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => { setApplyingId(t.id); handleApply(t); }}
                      disabled={applyingId === t.id}
                      style={{ fontSize: 11 }}
                    >
                      {applyingId === t.id ? "…" : "Apply"}
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => {
                        if (confirm(`Delete template "${t.name}"?`)) removeTemplate(t.id);
                      }}
                      title="Delete template"
                      style={{ fontSize: 14, padding: "2px 6px" }}
                      aria-label={`Delete template ${t.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="actions">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function summarize(t: ChannelTemplate): string {
  const parts: string[] = [];
  parts.push(t.mode === "brainstorm" ? "brainstorm" : "chat");
  if ((t.document ?? "").trim()) parts.push("whiteboard");
  const dn = t.attached_documents?.length ?? 0;
  if (dn > 0) parts.push(`${dn} doc${dn === 1 ? "" : "s"}`);
  if (t.brainstorm_mp_enabled) parts.push("MP");
  if (t.tools_enabled) parts.push("tools");
  return parts.join(" · ");
}
